/**
 * CSV Export Utility for Chart Components
 *
 * This utility provides functions to export chart data to CSV files with proper formatting,
 * internationalization support, and browser-compatible download functionality.
 *
 * Key Features:
 * - Automatic CSV escaping for special characters (commas, quotes, newlines)
 * - Configurable date formatting (locale, ISO, custom)
 * - UTF-8 BOM for Excel compatibility
 * - Type-safe interfaces for single and multi-series chart data
 * - Browser-native implementation (no external dependencies)
 * - Timestamped filename generation for unique file names
 *
 * @example
 * ```typescript
 * // Single-series chart export
 * exportChartToCSV(chartData, 'performance-data', 'Score');
 *
 * // Multi-series chart export
 * exportMultiSeriesChartToCSV(chartData, 'comparison-data', {
 *   date: 'Date',
 *   current: 'Current Year',
 *   comparison: 'Previous Year'
 * });
 *
 * // Full control export with timestamped filename
 * exportChartCSV({
 *   filename: getTimestampedFilename('custom-data'),
 *   headers: ['Date', 'Value'],
 *   data: transformedData,
 *   dateFormat: 'iso'
 * });
 * ```
 */

/**
 * Configuration options for CSV export
 */
interface CSVExportOptions {
  /** Base filename for the downloaded file (extension added automatically) */
  filename: string;
  /** Column headers for the CSV file */
  headers: string[];
  /** Data rows to export - each object represents one row */
  data: Record<string, unknown>[];
  /** How to format date values in the output */
  dateFormat?: "iso" | "locale" | "custom";
  /** Custom date formatting function (used when dateFormat is 'custom') */
  customDateFormatter?: (date: string | Date) => string;
  /** Field delimiter (defaults to comma) */
  delimiter?: string;
}

/**
 * Generates a timestamped filename for unique file identification.
 *
 * Creates a timestamp in the format YYYY-MM-DD-HH_MM and appends it to the base filename.
 * This ensures each exported file has a unique name and provides chronological sorting.
 *
 * @param baseFileName - The base name for the file (without extension)
 * @returns Filename with timestamp appended in format: `${baseFileName}-${timestamp}`
 *
 * @example
 * ```typescript
 * timestampedFilename('commitment-index-summary')
 * // Returns: 'commitment-index-summary-2024-01-15-14_30'
 *
 * timestampedFilename('sales-report')
 * // Returns: 'sales-report-2024-01-15-14_30'
 * ```
 */
export const timestampFilename = (baseFileName: string): string => {
  const now = new Date();
  const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}_${String(now.getMinutes()).padStart(2, "0")}`;
  return `${baseFileName}-${timestamp}`;
};

/**
 * Escapes CSV field values to handle commas, quotes, and newlines safely.
 *
 * CSV escaping rules:
 * - Fields containing commas, quotes, or newlines are wrapped in double quotes
 * - Double quotes within fields are escaped by doubling them (" becomes "")
 * - null/undefined values become empty strings
 *
 * @param field - The field value to escape
 * @returns Properly escaped string ready for CSV output
 *
 * @example
 * ```typescript
 * escapeCSVField('simple text')        // → 'simple text'
 * escapeCSVField('text, with comma')   // → '"text, with comma"'
 * escapeCSVField('text "with quotes"') // → '"text ""with quotes"""'
 * escapeCSVField(null)                 // → ''
 * ```
 */
const escapeCSVField = (field: unknown): string => {
  if (field === null || field === undefined) {
    return "";
  }

  const stringField = String(field);

  if (
    stringField.includes(",") ||
    stringField.includes('"') ||
    stringField.includes("\n")
  ) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }

  return stringField;
};

/**
 * Formats date values based on the specified format option.
 *
 * Handles various input types gracefully:
 * - String dates (ISO format, locale format, etc.)
 * - Date objects
 * - Invalid dates (returned as-is)
 * - Non-date values (converted to string)
 *
 * @param date - The date value to format
 * @param format - How to format the date ('iso', 'locale', or 'custom')
 * @param customFormatter - Custom formatting function (required if format is 'custom')
 * @returns Formatted date string
 *
 * @example
 * ```typescript
 * formatDate('2024-01-15', 'locale')     // → '1/15/2024' (US locale)
 * formatDate('2024-01-15', 'iso')        // → '2024-01-15T00:00:00.000Z'
 * formatDate('2024-01-15', 'custom', d => d.toLocaleDateString('en-GB'))  // → '15/01/2024'
 * formatDate('invalid', 'locale')        // → 'invalid'
 * formatDate(null, 'locale')             // → ''
 * ```
 */
const formatDate = (
  date: unknown,
  format: CSVExportOptions["dateFormat"] = "locale",
  customFormatter?: (date: string | Date) => string,
): string => {
  if (!date || (typeof date !== "string" && !(date instanceof Date))) {
    return String(date || "");
  }

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return String(date);
  }

  switch (format) {
    case "iso":
      return dateObj.toISOString();
    case "custom":
      return customFormatter
        ? customFormatter(dateObj)
        : dateObj.toLocaleDateString();
    case "locale":
    default:
      return dateObj.toLocaleDateString();
  }
};

/**
 * Converts data array to CSV string format with proper escaping and formatting.
 *
 * Process:
 * 1. Creates header row from provided headers
 * 2. Maps each data object to a CSV row
 * 3. Automatically detects and formats date fields
 * 4. Escapes all field values for CSV safety
 * 5. Joins rows with newlines
 *
 * @param headers - Column headers for the CSV
 * @param data - Array of data objects to convert
 * @param options - Formatting options (date format, delimiter)
 * @returns Complete CSV content as string
 *
 * @example
 * ```typescript
 * const csv = convertToCSV(
 *   ['Date', 'Score'],
 *   [{ date: '2024-01-01', score: 85 }],
 *   { dateFormat: 'locale' }
 * );
 * // Returns: "Date,Score\n1/1/2024,85"
 * ```
 */
const convertToCSV = (
  headers: string[],
  data: Record<string, unknown>[],
  options: Pick<
    CSVExportOptions,
    "dateFormat" | "customDateFormatter" | "delimiter"
  >,
): string => {
  const { dateFormat, customDateFormatter, delimiter = "," } = options;

  const headerRow = headers.map(escapeCSVField).join(delimiter);

  const dataRows = data.map((row) => {
    return headers
      .map((header) => {
        const value = row[header.toLowerCase()] || row[header];

        if (
          value &&
          (header.toLowerCase().includes("date") || value instanceof Date)
        ) {
          return escapeCSVField(
            formatDate(value, dateFormat, customDateFormatter),
          );
        }

        return escapeCSVField(value);
      })
      .join(delimiter);
  });

  return [headerRow, ...dataRows].join("\n");
};

/**
 * Triggers download of CSV file in the browser using the download attribute.
 *
 * Process:
 * 1. Creates a Blob with UTF-8 BOM for Excel compatibility
 * 2. Generates a temporary object URL
 * 3. Creates a hidden anchor element with download attribute
 * 4. Programmatically clicks the link to trigger download
 * 5. Cleans up the DOM element and object URL
 *
 * @param csvContent - The complete CSV content string
 * @param filename - Desired filename (will have .csv added if needed)
 *
 * @example
 * ```typescript
 * downloadCSV('Date,Score\n2024-01-01,85', 'performance-data');
 * // Downloads file named "performance-data.csv"
 * ```
 */
const downloadCSV = (csvContent: string, filename: string): void => {
  // Add BOM for proper UTF-8 encoding in Excel
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Main export function - converts data to CSV and triggers download.
 *
 * This is the core function that orchestrates the entire CSV export process.
 * It validates inputs, converts data to CSV format, and triggers the download.
 *
 * @param options - Complete configuration for the CSV export
 * @throws {Error} When CSV generation fails due to Blob creation issues
 *
 * @example
 * ```typescript
 * // Basic usage
 * exportChartCSV({
 *   filename: 'sales-data',
 *   headers: ['Date', 'Revenue', 'Units'],
 *   data: [
 *     { date: '2024-01-01', revenue: 1000, units: 50 },
 *     { date: '2024-01-02', revenue: 1200, units: 60 }
 *   ]
 * });
 *
 * // With timestamped filename
 * exportChartCSV({
 *   filename: timestampedFilename('quarterly-report'),
 *   headers: ['Quarter', 'Performance'],
 *   data: quarterlyData,
 *   dateFormat: 'custom',
 *   customDateFormatter: (date) => `Q${Math.ceil((date.getMonth() + 1) / 3)} ${date.getFullYear()}`
 * });
 * ```
 */
export const exportChartCSV = (options: CSVExportOptions): void => {
  const {
    filename,
    headers,
    data,
    dateFormat = "locale",
    customDateFormatter,
    delimiter = ",",
  } = options;

  if (!data || data.length === 0) {
    console.warn("No data provided for CSV export");
    return;
  }

  if (!headers || headers.length === 0) {
    console.warn("No headers provided for CSV export");
    return;
  }

  try {
    const csvContent = convertToCSV(headers, data, {
      dateFormat,
      customDateFormatter,
      delimiter,
    });

    downloadCSV(csvContent, filename);
  } catch (error) {
    console.error("Error exporting CSV:", error);
    throw new Error("Failed to export CSV file");
  }
};

/**
 * Helper function for single-series chart export.
 *
 * Simplified interface for the most common use case: time series charts with
 * date and value columns. Automatically maps chart data to the expected format.
 *
 * @param chartData - Array of chart data points with date and value
 * @param filename - Base filename for the download
 * @param valueLabel - Label for the value column (defaults to 'Value')
 *
 * @example
 * ```typescript
 * // Commit score chart
 * exportChartToCSV(commitScoreData, 'commit-scores', 'Commit Score');
 *
 * // Revenue chart with default label
 * exportChartToCSV(revenueData, 'monthly-revenue'); // Value column will be "Value"
 * ```
 */
export const exportChartToCSV = (
  chartData: { date: string | Date; value: number; [key: string]: unknown }[],
  filename: string,
  valueLabel = "Value",
): void => {
  exportChartCSV({
    filename,
    headers: ["Date", valueLabel],
    data: chartData.map((point) => ({
      date: point.date,
      [valueLabel.toLowerCase()]: point.value,
    })),
    dateFormat: "locale",
  });
};

/**
 * Helper function for multi-series chart export (current vs comparison data).
 *
 * Designed for charts that compare current period data against a comparison period
 * (e.g., year-over-year, month-over-month comparisons). Handles missing comparison
 * values gracefully by using empty strings.
 *
 * @param chartData - Array of chart data with current and optional comparison values
 * @param filename - Base filename for the download
 * @param headers - Internationalized headers for date, current, and comparison columns
 *
 * @example
 * ```typescript
 * // Year-over-year revenue comparison
 * exportMultiSeriesChartToCSV(revenueData, 'revenue-comparison', {
 *   date: t('charts.headers.date'),
 *   current: t('charts.headers.currentYear'),
 *   comparison: t('charts.headers.previousYear')
 * });
 *
 * // Performance metrics with some missing comparison data
 * const performanceData = [
 *   { date: '2024-01-01', current: 85, comparison: 78 },
 *   { date: '2024-01-02', current: 92, comparison: undefined } // Will export as empty
 * ];
 * ```
 */
export const exportMultiSeriesChartToCSV = (
  chartData: {
    date: string | Date;
    current: number;
    comparison?: number;
    [key: string]: unknown;
  }[],
  filename: string,
  headers: {
    date: string;
    current: string;
    comparison: string;
  },
): void => {
  exportChartCSV({
    filename,
    headers: [headers.date, headers.current, headers.comparison],
    data: chartData.map((point) => ({
      [headers.date.toLowerCase()]: point.date,
      [headers.current.toLowerCase()]: point.current,
      [headers.comparison.toLowerCase()]: point.comparison ?? "",
    })),
    dateFormat: "locale",
  });
};

export type { CSVExportOptions };
